import { Scene } from 'phaser'

export class PausedScene extends Scene {
  private background!: Phaser.GameObjects.Rectangle
  private title!: Phaser.GameObjects.Text
  private buttons: Phaser.GameObjects.Container[] = []

  constructor() {
    super({ key: 'PausedScene' })
  }

  create() {
    const { width, height } = this.cameras.main

    // Create semi-transparent overlay
    this.background = this.add.rectangle(0, 0, width, height, 0x000000, 0.7)
    this.background.setOrigin(0, 0)

    // Create modal background
    const modalBg = this.add.rectangle(width / 2, height / 2, 400, 300, 0x1e293b)
    modalBg.setStrokeStyle(2, 0xf59e0b)
    modalBg.setOrigin(0.5, 0.5)

    // Title
    this.title = this.add.text(width / 2, height / 2 - 80, 'GAME PAUSED', {
      fontSize: '36px',
      fontFamily: 'Arial, sans-serif',
      color: '#f59e0b',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // Subtitle
    const subtitle = this.add.text(width / 2, height / 2 - 30, 'Game is paused', {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#94a3b8'
    }).setOrigin(0.5)

    // Create buttons
    this.createButtons(width / 2, height / 2 + 30)

    // Add instructions
    this.addInstructions(width / 2, height / 2 + 140)

    // Add visual effects
    this.addVisualEffects()

    // Setup input handling
    this.setupInputHandlers()
  }

  private createButtons(x: number, y: number): void {
    // Resume button
    const resumeButton = this.add.rectangle(x, y - 40, 180, 50, 0x22c55e)
    resumeButton.setStrokeStyle(2, 0x16a34a)
    resumeButton.setInteractive()

    const resumeText = this.add.text(x, y - 40, 'Resume', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    resumeButton.on('pointerover', () => {
      resumeButton.setFillStyle(0x16a34a)
      resumeButton.setStrokeStyle(2, 0x15803d)
    })

    resumeButton.on('pointerout', () => {
      resumeButton.setFillStyle(0x22c55e)
      resumeButton.setStrokeStyle(2, 0x16a34a)
    })

    resumeButton.on('pointerdown', () => {
      this.resumeGame()
    })

    this.buttons.push(this.add.container(x, y - 40, [resumeButton, resumeText]))

    // Settings button
    const settingsButton = this.add.rectangle(x, y + 40, 180, 50, 0x475569)
    settingsButton.setStrokeStyle(2, 0x64748b)
    settingsButton.setInteractive()

    const settingsText = this.add.text(x, y + 40, 'Settings', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    settingsButton.on('pointerover', () => {
      settingsButton.setFillStyle(0x64748b)
      settingsButton.setStrokeStyle(2, 0x475569)
    })

    settingsButton.on('pointerout', () => {
      settingsButton.setFillStyle(0x475569)
      settingsButton.setStrokeStyle(2, 0x64748b)
    })

    settingsButton.on('pointerdown', () => {
      this.openSettings()
    })

    this.buttons.push(this.add.container(x, y + 40, [settingsButton, settingsText]))

    // Main Menu button
    const menuButton = this.add.rectangle(x, y + 120, 180, 50, 0xef4444)
    menuButton.setStrokeStyle(2, 0xdc2626)
    menuButton.setInteractive()

    const menuText = this.add.text(x, y + 120, 'Main Menu', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    menuButton.on('pointerover', () => {
      menuButton.setFillStyle(0xdc2626)
      menuButton.setStrokeStyle(2, 0xb91c1c)
    })

    menuButton.on('pointerout', () => {
      menuButton.setFillStyle(0xef4444)
      menuButton.setStrokeStyle(2, 0xdc2626)
    })

    menuButton.on('pointerdown', () => {
      this.backToMenu()
    })

    this.buttons.push(this.add.container(x, y + 120, [menuButton, menuText]))
  }

  private addInstructions(x: number, y: number): void {
    const instructions = this.add.text(x, y, 'Press ESC or SPACE to resume', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#64748b'
    }).setOrigin(0.5)
  }

  private addVisualEffects(): void {
    const { width, height } = this.cameras.main

    // Add some ambient particles
    for (let i = 0; i < 10; i++) {
      const x = Math.random() * width
      const y = Math.random() * height
      const size = Math.random() * 3 + 1

      const particle = this.add.circle(x, y, size, 0xf59e0b, 0.2)

      this.tweens.add({
        targets: particle,
        alpha: { from: 0.2, to: 0.5 },
        duration: 2000,
        ease: 'Sine.easeInOut',
        repeat: -1,
        yoyo: true
      })
    }
  }

  private setupInputHandlers(): void {
    // Keyboard shortcuts
    const escKey = this.input.keyboard!.addKey('ESC')
    const spaceKey = this.input.keyboard!.addKey('SPACE')

    escKey.on('down', () => {
      this.resumeGame()
    })

    spaceKey.on('down', () => {
      this.resumeGame()
    })
  }

  private resumeGame(): void {
    // Resume the playing scene
    const playingScene = this.scene.get('PlayingScene')
    if (playingScene) {
      playingScene.scene.resume()
    }

    // Stop this scene
    this.scene.stop()
  }

  private openSettings(): void {
    console.log('Opening settings...')
    // This would open a settings modal or scene
    // For now, just log it
  }

  private backToMenu(): void {
    // Stop match scenes and let React shell reset via game event.
    this.scene.stop('PlayingScene')
    this.scene.stop()
    this.game.events.emit('resetGame')
  }
}